import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/apiClient';
import {
  type Incident,
  type ThreatArticle,
  type DailyAlert,
} from '../data/demoData';
import type { DashboardTimeRange } from '../lib/timeRange';

export interface SecureScoreData {
  source: string;
  currentScore: number;
  maxScore: number;
  percentage: number;
  trend: number;
  categoryScores: { name: string; percentage: number; current: number; max: number; controlCount: number }[];
  recommendations: unknown[];
  actionCounts: { toAddress: number; riskAccepted: number; resolved: number; regressed: number };
  history: { date: string; percentage: number }[];
}

export interface LiveDashboardData {
  source: 'live' | 'partial' | 'none';
  incidentSource: string;
  incidents: Incident[];
  alertVolume: DailyAlert[];
  secureScore: SecureScoreData | null;
  mdtiArticles: ThreatArticle[];
  fetchedAt: string;
  errors: string[];
  /** Set by backend when the requested customerId no longer exists. */
  customerNotFound?: boolean;
}

export interface UseLiveDataResult {
  incidents: Incident[];
  dailyAlerts: DailyAlert[];
  threatArticles: ThreatArticle[];
  secureScore: SecureScoreData | null;
  source: 'live' | 'loading' | 'empty';
  incidentSource: string;
  isLoading: boolean;
  error: string | null;
  customerError: string | null;
  lastRefresh: string;
  refresh: (force?: boolean) => void;
  azureConfigured: boolean;
}

const EMPTY_DAILY_ALERTS: DailyAlert[] = [];

export function useLiveData(
  autoRefreshSeconds = 300,
  customerId?: string,
  days: DashboardTimeRange = '24h',
  onCustomerNotFound?: () => void,
): UseLiveDataResult {
  const [source, setSource] = useState<'live' | 'loading' | 'empty'>('loading');
  const [incidentSource, setIncidentSource] = useState('');
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [dailyAlerts, setDailyAlerts] = useState<DailyAlert[]>(EMPTY_DAILY_ALERTS);
  const [threatArticles, setThreatArticles] = useState<ThreatArticle[]>([]);
  const [secureScore, setSecureScore] = useState<SecureScoreData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState('');
  const [azureConfigured, setAzureConfigured] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Bumped on every fetchLive() call so a slower, superseded request (e.g. the
  // old 4h query still in flight when the analyst switches to 24h) can detect
  // it's no longer the latest and skip applying its stale response.
  const requestSeqRef = useRef(0);

  const fetchLive = useCallback(async (force = false) => {
    const mySeq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    setCustomerError(null);
    try {
      const params = new URLSearchParams();
      if (force) params.set('refresh', 'true');
      if (customerId) params.set('customerId', customerId);
      params.set('days', String(days));
      const data = await apiFetch<LiveDashboardData>(`/dashboard-data?${params.toString()}`);

      // A newer fetchLive() call has already started -- discard this response.
      if (mySeq !== requestSeqRef.current) return;

      // Self-heal: if the backend reports the requested customerId is gone,
      // notify the parent so it can refresh the customer list and switch back
      // to a valid (default) workspace.
      if (data.customerNotFound) {
        setCustomerError(data.errors?.[0] ?? 'Customer no longer exists');
        setSource('empty');
        setIncidents([]);
        setDailyAlerts(EMPTY_DAILY_ALERTS);
        setSecureScore(null);
        if (onCustomerNotFound) onCustomerNotFound();
        return;
      }

      // Kloudynet TI (rosti.dev) is independent of Azure credentials - show it
      // whenever the backend successfully returned articles, even if no
      // incidents / secure score were available for this customer.
      if (data.mdtiArticles && data.mdtiArticles.length > 0) {
        setThreatArticles(data.mdtiArticles as ThreatArticle[]);
      }

      if (data.source === 'none') {
        setSource('empty');
        setIncidents([]);
        setDailyAlerts(EMPTY_DAILY_ALERTS);
        setSecureScore(null);
        const errMsg = data.errors?.length
          ? data.errors[0]
          : customerId ? 'Could not connect to this customer. Check credentials and Azure permissions.' : null;
        if (errMsg) {
          if (customerId) setCustomerError(errMsg);
          else setError(errMsg);
        }
      } else {
        setSource('live');
        setIncidentSource(data.incidentSource);
        setIncidents(data.incidents as Incident[]);
        if (data.alertVolume.length > 0) setDailyAlerts(data.alertVolume);
        setSecureScore(data.secureScore);
        if (data.errors?.length) {
          const msg = data.errors.slice(0, 2).join(' | ');
          if (customerId) setCustomerError(`Partial data: ${msg}`);
          else setError(`Partial data: ${msg}`);
        }
      }
    } catch (e) {
      if (mySeq !== requestSeqRef.current) return;
      setError(`API unreachable: ${String(e)}`);
      setSource('empty');
    } finally {
      if (mySeq === requestSeqRef.current) {
        setIsLoading(false);
        setLastRefresh(new Date().toLocaleTimeString());
      }
    }
  }, [customerId, days]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run once on mount: check if Azure is configured ─────────────────────
  useEffect(() => {
    apiFetch<{ clientId?: string }>('/config')
      .then((d) => {
        const configured = !!d.clientId;
        setAzureConfigured(configured);
        if (!configured) { setSource('empty'); setIsLoading(false); }
      })
      .catch(() => { setSource('empty'); setIsLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Refetch whenever Azure becomes available, customer changes, or date-window changes ──
  useEffect(() => {
    if (!azureConfigured && !customerId) return;
    setSource('loading');
    setIncidents([]);
    setDailyAlerts(EMPTY_DAILY_ALERTS); // clear stale chart data when switching tenant/window
    setSecureScore(null);
    setCustomerError(null);
    setError(null);
    fetchLive();
  }, [azureConfigured, fetchLive]); // fetchLive identity changes when customerId/days change

  // ── Auto-refresh timer ────────────────────────────────────────────────────
  // Pauses while the tab is hidden - there's no point burning KQL quota on a
  // dashboard the analyst can't see. Resumes on visibilitychange and triggers
  // an immediate refetch so the user sees fresh data when they come back.
  useEffect(() => {
    if (!azureConfigured || autoRefreshSeconds <= 0) return;

    function startTimer(): void {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => fetchLive(), autoRefreshSeconds * 1000);
    }
    function stopTimer(): void {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }

    if (document.visibilityState === 'visible') startTimer();

    function onVisibility(): void {
      if (document.visibilityState === 'visible') {
        fetchLive();    // catch up on missed ticks
        startTimer();
      } else {
        stopTimer();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stopTimer();
    };
  }, [azureConfigured, autoRefreshSeconds, fetchLive]);

  const refresh = useCallback((force = false) => {
    if (azureConfigured || customerId) fetchLive(force);
    else setLastRefresh(new Date().toLocaleTimeString());
  }, [azureConfigured, customerId, fetchLive]);

  return { incidents, dailyAlerts, threatArticles, secureScore, source, incidentSource, isLoading, error, customerError, lastRefresh, refresh, azureConfigured };
}
